import { useState } from "react";
import SectionLabel from "../ui/SectionLabel";
import StaggerContainer from "../motion/StaggerContainer";
import TiltCard from "../motion/TiltCard";
import PretextReveal from "../motion/PretextReveal";

const services = [
  {
    num: "I",
    title: "Web &\nDigital",
    items: [
      "Business Websites & Web Apps",
      "E-Commerce (Shopify, WooCommerce, Custom)",
      "SEO & Organic Growth",
      "Google & Meta Ads Management",
      "Social Media Management",
      "Branding & Visual Identity",
      "UI/UX Design",
    ],
    cta: "Explore Web Services",
    href: "/services/web-development",
  },
  {
    num: "II",
    title: "Software &\nEnterprise",
    items: [
      "Custom CRM Development",
      "Custom ERP Development",
      "Web & Mobile App Development",
      "Backend & API Development",
      "Cloud & DevOps (AWS, GCP)",
      "Database Architecture",
      "Third-Party Integrations",
    ],
    cta: "Explore Software",
    href: "/services/crm-development",
  },
  {
    num: "III",
    title: "AI &\nAutomation",
    items: [
      "AI Agents & Chatbots",
      "AI Call Centre (Inbound + Outbound)",
      "LLM Integration (GPT-4o, Claude, Gemini)",
      "RAG Pipelines & Vector Databases",
      "RPA & Workflow Automation",
      "AI Strategy Consulting",
      "Custom AI Model Development",
    ],
    cta: "Explore AI",
    href: "/services/ai-agents",
  },
];

export default function ServicesOverview() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <>
      <section className="owl-services-section" style={{ background: "#050505", padding: "120px 40px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div className="owl-services-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 48, gap: 24 }}>
            <SectionLabel text="OUR CAPABILITIES" />
            <PretextReveal
              mode="wave"
              as="p"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 300,
                fontSize: 16,
                color: "#908A83",
              }}
              delay={0.2}
            >
              Three verticals. One team. Full accountability.
            </PretextReveal>
          </div>

          <div style={{ marginBottom: 80 }}>
            <PretextReveal
              mode="cascade"
              as="div"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 300,
                fontSize: "clamp(48px, 6vw, 80px)",
                color: "#F8F4EC",
                lineHeight: 0.9,
              }}
              delay={0.1}
              hoverMode="magnetic"
            >
              Everything your
            </PretextReveal>
            <PretextReveal
              mode="cascade"
              as="div"
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(48px, 6vw, 80px)",
                color: "#D4A843",
                lineHeight: 0.95,
              }}
              delay={0.2}
              hoverMode="magnetic"
            >
              business needs.
            </PretextReveal>
          </div>

          <StaggerContainer stagger={0.15} direction="up" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }} className="owl-services-grid">
            {services.map((service, index) => (
              <div
                key={service.num}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
              >
              <TiltCard
                className="owl-services-tiltcard"
                style={{
                  padding: "0 48px",
                  cursor: "pointer",
                  borderLeft: index > 0 ? "0.5px solid rgba(212,168,67,0.2)" : "none",
                  background: hovered === index ? "#0E0E0E" : "transparent",
                  transition: "background 0.3s ease",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.2em",
                    color: "#D4A843",
                    marginBottom: 16,
                  }}
                >
                  {service.num}
                </div>
                <div
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 300,
                    fontSize: "clamp(32px, 3.5vw, 52px)",
                    color: "#F8F4EC",
                    lineHeight: 1.1,
                    whiteSpace: "pre-line",
                    marginBottom: 32,
                  }}
                >
                  {service.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", marginBottom: 32 }}>
                  {service.items.map((item, itemIndex) => (
                    <div
                      key={item}
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 300,
                        fontSize: 15,
                        color: hovered === index ? "#F8F4EC" : "#908A83",
                        padding: "10px 0",
                        borderBottom: "0.5px solid rgba(212,168,67,0.08)",
                        transition: `color 0.2s ease ${itemIndex * 0.03}s`,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <span style={{ color: "#D4A843", fontSize: 14, opacity: 0.8 }}>-</span>
                      {item}
                    </div>
                  ))}
                </div>
                <a
                  href={service.href}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 500,
                    fontSize: 14,
                    color: "#D4A843",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 8,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = "none";
                  }}
                >
                  {service.cta} →
                </a>
              </TiltCard>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <style>{`
        @media (max-width: 960px) {
          .owl-services-head {
            flex-direction: column;
            align-items: flex-start !important;
          }

          .owl-services-grid {
            grid-template-columns: 1fr !important;
            gap: 40px;
          }

          .owl-services-grid > div {
            padding: 0 !important;
            border-left: none !important;
          }

          .owl-services-tiltcard {
            padding: 0 !important;
          }
        }

        @media (max-width: 640px) {
          .owl-services-section {
            padding: 64px clamp(20px, 5vw, 32px) !important;
          }
        }
      `}</style>
    </>
  );
}
